import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalFooter,
  Typography
} from '@one-thd/sui-atomic-components';
import { ProductListContext } from '../ProductListContext';

const {
  bool,
  func,
  arrayOf,
  string
} = PropTypes;

export const DeleteModal = ({
  isOpen,
  onClose,
  selectedItems = [],
  selectedGroups = [],
  onDeleteSuccess
}) => {
  const { list, removeListItems } = useContext(ProductListContext);
  const [removalMessaging, setRemovalMessaging] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setRemovalMessaging(''); // Clear the message when closing
    onClose();
  };

  const handleSubmit = async () => {
    setIsDeleting(true);

    // Close the modal immediately, don't wait for the async operation
    handleClose();

    try {
      // Combine all selected items and groups for deletion
      const allSelectedIds = [
        ...selectedItems,
        ...selectedGroups
      ];

      if (onDeleteSuccess) {
        await onDeleteSuccess();
      } else {
        // Use the removeListItems from context
        await removeListItems(allSelectedIds.length > 0 ? allSelectedIds : null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting items/groups:', error);
    } finally {
      setIsDeleting(false);
    }
  }; // Calculate messaging based on what's selected - only run once when modal opens
  useEffect(() => {
    // Only calculate messaging if the modal is open and we have selections
    if (!isOpen || (selectedGroups.length === 0 && selectedItems.length === 0)) {
      return;
    }

    // Filter out Default Group (__ungrouped__) from group count
    const actualGroups = selectedGroups.filter((groupId) => groupId !== '__ungrouped__');
    const hasGroups = actualGroups.length > 0;
    const hasItems = selectedItems.length > 0;

    // Count items in selected groups (including Default Group for item count)
    let itemsInGroups = 0;
    if (selectedGroups.length > 0 && list?.groups) {
      selectedGroups.forEach((groupId) => {
        // Convert __ungrouped__ back to null for lookup
        const actualGroupId = groupId === '__ungrouped__' ? null : groupId;
        const group = list.groups.find((groupItem) => groupItem.groupId === actualGroupId);
        if (group?.items) {
          itemsInGroups += group.items.length;
        }
      });
    }

    if (hasGroups && hasItems) {
      const groupText = actualGroups.length === 1 ? 'group' : 'groups';
      const itemText = selectedItems.length === 1 ? 'item' : 'items';
      const message = `Are you sure you want to remove ${actualGroups.length} ${groupText} `
        + `and ${selectedItems.length} ${itemText}?`;
      setRemovalMessaging(message);
    } else if (hasGroups && !hasItems) {
      if (actualGroups.length === 1) {
        // Convert __ungrouped__ back to null for lookup
        const actualGroupId = actualGroups[0] === '__ungrouped__' ? null : actualGroups[0];
        const group = list?.groups?.find((groupItem) => groupItem.groupId === actualGroupId);
        const groupName = group?.groupName || 'group';
        const itemCount = group?.items?.length || 0;

        if (itemCount > 0) {
          const itemText = itemCount === 1 ? 'item' : 'items';
          setRemovalMessaging(`Are you sure you want to remove "${groupName}" and its ${itemCount} ${itemText}?`);
        } else {
          setRemovalMessaging(`Are you sure you want to remove the empty group "${groupName}"?`);
        }
      } else {
        const totalItems = itemsInGroups;
        if (totalItems > 0) {
          const groupText = actualGroups.length === 1 ? 'group' : 'groups';
          const itemText = totalItems === 1 ? 'item' : 'items';
          const message = `Are you sure you want to remove ${actualGroups.length} ${groupText} `
            + `and their ${totalItems} ${itemText}?`;
          setRemovalMessaging(message);
        } else {
          const groupText = actualGroups.length === 1 ? 'group' : 'groups';
          setRemovalMessaging(`Are you sure you want to remove ${actualGroups.length} empty ${groupText}?`);
        }
      }
    } else if (!hasGroups && hasItems) {
      const itemText = selectedItems.length === 1 ? 'item' : 'items';
      setRemovalMessaging(`Are you sure you want to remove ${selectedItems.length} ${itemText}?`);
    } else if (!hasGroups && !hasItems && selectedGroups.includes('__ungrouped__')) {
      // Special case: Only Default Group is selected (no actual groups, but items in default group)
      const defaultGroup = list?.groups?.find((groupItem) => groupItem.groupId === null);
      const itemCount = defaultGroup?.items?.length || 0;

      if (itemCount > 0) {
        const itemText = itemCount === 1 ? 'item' : 'items';
        setRemovalMessaging(`Are you sure you want to remove all ${itemCount} ${itemText} from the Default Group?`);
      } else {
        setRemovalMessaging('No items to remove.');
      }
    }
  }, [isOpen]); // Only depend on isOpen to prevent re-runs during deletion

  // Clear message when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setRemovalMessaging('');
      setIsDeleting(false);
    }
  }, [isOpen]);

  // Don't render anything if modal is not open
  if (!isOpen) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={handleClose} title="Confirm Removal">
      <ModalBody>
        <Typography weight="bold" align="center">
          {removalMessaging}
        </Typography>
      </ModalBody>
      <ModalFooter>
        <ButtonGroup fullWidth>
          <Button
            fullWidth
            onClick={handleClose}
            variant="secondary"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            onClick={handleSubmit}
            variant="primary"
            disabled={isDeleting}
          >
            {isDeleting ? 'Removing...' : 'Remove'}
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  );
};

DeleteModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  selectedItems: arrayOf(string),
  selectedGroups: arrayOf(string),
  onDeleteSuccess: func
};

DeleteModal.defaultProps = {
  selectedItems: [],
  selectedGroups: [],
  onDeleteSuccess: null
};
